import React, { createContext, useContext, useState, useCallback } from 'react';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import FeedbackModal from 'talent_matching/components/modals/leave_feedback_modal';
import RejectModal from 'candidate_profile_redesign/modals/reject/reject_modal';
import { Person, Row } from 'talent_matching/types';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';

type ActionSource = 'table_row' | 'side_panel';

type RowActionsModalContextValue = {
  openFeedbackFor: (candidate: Person) => void;
  openRejectFor: (candidate: Person, actionSource: ActionSource) => void;
};

const RowActionsModalContext = createContext<
  RowActionsModalContextValue | undefined
>(undefined);

export const useRowActionsModal = () => {
  const context = useContext(RowActionsModalContext);
  if (!context) {
    throw new Error(
      'useRowActionsModal must be used within RowActionsModalProvider'
    );
  }
  return context;
};

type RowActionsModalProviderProps = {
  onRejectSuccess: (row: Row, closeModal: () => void) => void;
  children: React.ReactNode;
};

export const RowActionsModalProvider = ({
  onRejectSuccess,
  children,
}: RowActionsModalProviderProps) => {
  const { paths, appReviewSessionId } = useTalentMatchingTabContext();
  const [feedbackCandidate, setFeedbackCandidate] = useState<Person | null>(
    null
  );
  const [rejectCandidate, setRejectCandidate] = useState<Person | null>(null);
  const [rejectActionSource, setRejectActionSource] =
    useState<ActionSource>('table_row');

  const [showFeedbackModal, openFeedbackModal, closeFeedbackModal] = useModal();
  const [rejectModalOpen, openRejectModal, closeRejectModal] = useModal();

  const openFeedbackFor = useCallback(
    (candidate: Person) => {
      setFeedbackCandidate(candidate);
      openFeedbackModal();
    },
    [openFeedbackModal]
  );

  const openRejectFor = useCallback(
    (candidate: Person, actionSource: ActionSource) => {
      setRejectCandidate(candidate);
      setRejectActionSource(actionSource);
      openRejectModal();
    },
    [openRejectModal]
  );

  const handleCloseFeedback = useCallback(() => {
    closeFeedbackModal();
    setFeedbackCandidate(null);
  }, [closeFeedbackModal]);

  const handleCloseReject = useCallback(() => {
    closeRejectModal();
    setRejectCandidate(null);
  }, [closeRejectModal]);

  return (
    <RowActionsModalContext.Provider value={{ openFeedbackFor, openRejectFor }}>
      {children}

      {feedbackCandidate && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          closeModal={handleCloseFeedback}
          application={feedbackCandidate}
          hiringPlanTagsPath={paths.hiringPlanTagsPath}
        />
      )}

      {rejectCandidate && (
        <RejectModal
          bulkMode={false}
          closeRejectModal={handleCloseReject}
          isRejectModalOpen={rejectModalOpen}
          getRejectModalPath={rejectCandidate.rejectModalPath}
          candidateName={rejectCandidate.fullName}
          changeApplicationStatePath={rejectCandidate.rejectPath}
          rejectionTrackingPayload={{
            app_review_session_id: appReviewSessionId,
            source: 'talent_matching',
            action_source: rejectActionSource,
          }}
          onRejectSuccess={() => {
            onRejectSuccess(rejectCandidate, handleCloseReject);
          }}
        />
      )}
    </RowActionsModalContext.Provider>
  );
};
